import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PDFViewer from '@/components/PDFViewer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, FileText, Upload, Clock, HelpCircle } from 'lucide-react';
import { trackHpdDrawCtaClicked } from '@/lib/analytics';

const SketchToCADConversionUK = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://houseplandirect.com';
  
  // Reuse the same 5 examples from Gallery
  const examples = [
    {
      id: 1,
      title: "Residential Property - First Floor",
      caption: "Hand-drawn sketch converted to professional CAD floor plan with accurate room dimensions.",
      beforeImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/hto61qm2_Example%201%20sketch.pdf",
        alt: "Hand-drawn floor plan sketch showing room layout and basic measurements",
        type: "pdf",
        rotation: 180
      },
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ot5fw0k5_Example%201%20Completed.pdf",
        alt: "Professional CAD floor plan with precise dimensions, room labels, and planning-ready output",
        type: "pdf"
      }
    },
    {
      id: 2,
      title: "Family Home - Ground Floor",
      caption: "Multiple sketch pages combined into a single professional floor plan.",
      beforeImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/irjnp6u7_Example%202%20Sketch%20a.JPG",
        alt: "Hand-drawn sketch of family home ground floor showing kitchen, living room and hallway",
        type: "image"
      },
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/a077anxi_Example%202%20Completed.pdf",
        alt: "Professional CAD floor plan of family home with accurate wall positions and door swings",
        type: "pdf",
        rotation: 270
      }
    },
    {
      id: 3,
      title: "Leasehold Property Plan",
      caption: "Sketch converted to official leasehold plan suitable for legal documentation.",
      beforeImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ox7djran_Example%203%20sketch%20a.JPG",
        alt: "Hand-drawn leasehold property sketch with boundary markings",
        type: "image"
      },
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/iifmqtro_Example%203%20leasehold%20plan%20completed.pdf",
        alt: "Professional leasehold CAD plan with location map and floor plan for legal submission",
        type: "pdf",
        rotation: 270
      }
    },
    {
      id: 4,
      title: "3D Colour Visualisation",
      caption: "Optional 3D colour rendering showing furniture layout and room styling.",
      beforeImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/l7rmhd52_Example%204%20sketch.JPG",
        alt: "Hand-drawn sketch with room measurements and layout notes",
        type: "image"
      },
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/ixt1i3oe_Example%204%203d%20colour%20completed.jpg",
        alt: "3D colour floor plan with furniture placement and room styling visualisation",
        type: "image"
      }
    },
    {
      id: 5,
      title: "Licence Plan",
      caption: "Professional licence plan for commercial property regulatory submissions.",
      beforeImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/wur75ec4_Example%205%20conversion%20and%20update%20sketch.JPG",
        alt: "Hand-drawn sketch for licence application showing property layout",
        type: "image"
      },
      afterImage: {
        url: "https://customer-assets.emergentagent.com/job_2f037581-fa96-41c0-ad02-21d6d4ac26c1/artifacts/rr4ftk7v_Example%205%20Licence%20plan%20completed.pdf",
        alt: "Professional licence plan CAD drawing suitable for council and regulatory submission",
        type: "pdf",
        rotation: 270
      }
    }
  ];

  const faqs = [
    {
      question: "Can you convert hand-drawn sketches to CAD?",
      answer: "Yes, we specialise in converting hand-drawn sketches, photos of plans, and even rough measurements into professional CAD floor plans. Your sketch doesn't need to be perfect - just clear enough to show the room layout."
    },
    {
      question: "Do you supply DWG and PDF?",
      answer: "Yes, all orders include your floor plan in PDF format (for printing and sharing), JPEG format (for digital use), and DWG format (editable CAD file for architects and builders)."
    },
    {
      question: "Are these suitable for planning permission?",
      answer: "Our CAD floor plans are designed to meet the requirements for planning applications. They include accurate dimensions, room labels, and professional presentation suitable for council submissions."
    },
    {
      question: "What if my sketch has no measurements?",
      answer: "We recommend including measurements for accuracy. If you're unsure how to measure, purchase our £2 measuring guide which includes step-by-step instructions. Basic plans can be produced from sketches without measurements, but dimensions won't be included."
    },
    {
      question: "How long does it take?",
      answer: "Standard turnaround is 5-7 working days. Most straightforward conversions are completed within this timeframe. We'll contact you if we need any clarification on your sketch."
    },
    {
      question: "What areas do you cover?",
      answer: "We're an online service covering the whole of the UK. Simply upload your sketch and we'll convert it - no site visit required for most projects."
    },
    {
      question: "Can I request revisions?",
      answer: "Yes, we correct any conversion errors at no extra charge. If we've misinterpreted your sketch, we'll fix it. Additional design changes beyond the original scope may incur a fee."
    },
    {
      question: "What file formats do you accept?",
      answer: "We accept photos (JPEG, PNG), PDFs, and scanned documents. You can upload pictures taken on your phone - just ensure the sketch is clearly visible and in focus."
    }
  ];

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Sketch to CAD Conversion UK",
    "serviceType": "Architectural Drafting Service",
    "description": "Professional sketch to CAD floor plan conversion service. Upload your hand-drawn sketch and receive planning-ready CAD drawings in PDF and DWG formats. UK-wide online service with fixed pricing from £40.",
    "url": `${baseUrl}/sketch-to-cad-conversion-uk`,
    "provider": {
      "@type": "Organization",
      "name": "House Plan Direct",
      "url": baseUrl
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "40",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "40",
        "priceCurrency": "GBP",
        "description": "Fixed price from £40 for up to 3 rooms"
      }
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="sketch-to-cad-conversion-uk"
        defaultTitle="Sketch to CAD Conversion UK | House Plan Direct"
        defaultDescription="Upload your sketch and get professional floor plans. PDF and DWG options, clear pricing, fast turnaround. UK-wide service."
      />
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Sketch to CAD Conversion UK
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Upload your hand-drawn sketch and receive professional CAD floor plans. 
            Fast, affordable, and ready for planning applications across the UK.
          </p>
          <Link to="/order">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
              Upload Your Sketch <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Cross-link to the free DIY toolkit — additive only, does not alter the managed-service content below */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="diy-alternative-callout">
          <p className="text-sm text-gray-700">
            Prefer to draw it yourself instead of ordering a conversion? HPD Draw lets you create, measure and present your own floor plan online — free to build and edit.
          </p>
          <Link to="/floor-plan-creator" onClick={() => trackHpdDrawCtaClicked('sketch_to_cad_diy', 'Start HPD Draw Free', 'sketch-to-cad-conversion-uk', 'general')}>
            <Button variant="outline" size="sm" className="mt-3 border-emerald-600 text-emerald-700 hover:bg-emerald-100" data-testid="sketch-to-cad-try-diy-btn">
              Start HPD Draw Free
            </Button>
          </Link>
        </div>
      </div>

      {/* What You Get */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What you get</h2>
          <p className="text-gray-600 mb-6">
            When you submit your sketch, we convert it into professional CAD floor plans that you can use for planning applications, builders quotes, estate agents, or your own records.
          </p>
          <ul className="space-y-4 mb-6">
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Professional floor plan(s) from your sketch, photo, or PDF</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-700 font-medium">PDF output</span>
                <span className="text-gray-600"> – printable, easy to share with councils and contractors</span>
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-700 font-medium">DWG output</span>
                <span className="text-gray-600"> – editable CAD file for architects and builders</span>
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Clear dimensions shown (if provided on your sketch)</span>
            </li>
          </ul>
          <p className="text-gray-600 text-sm bg-gray-50 p-4 rounded-lg">
            <strong>Note:</strong> We convert your sketch accurately. We do not create architectural designs or provide structural calculations. If you need design services or structural sign-off, we recommend consulting a qualified architect.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">How it works</h2>
          <ol className="space-y-6">
            {[
              { step: 1, title: "Upload your sketch or plan", desc: "Submit your hand-drawn sketch, photo, or existing plan through our simple order form." },
              { step: 2, title: "We confirm the scope", desc: "If anything is unclear, we'll contact you to clarify before starting work." },
              { step: 3, title: "Draft conversion to CAD", desc: "Our team converts your sketch into a professional CAD floor plan." },
              { step: 4, title: "Quality check", desc: "We review the conversion for accuracy and presentation." },
              { step: 5, title: "Delivery", desc: "Receive your completed floor plan by email in PDF, JPEG, and DWG formats." }
            ].map((item) => (
              <li key={item.step} className="flex items-start">
                <span className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white font-bold rounded-full mr-4 flex-shrink-0">
                  {item.step}
                </span>
                <div>
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Pricing</h2>
          <p className="text-gray-600 mb-6">
            We offer fixed pricing with no hidden fees. You'll know exactly what you're paying before you order.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="text-center">
              <p className="text-gray-600 mb-2">Floor plan conversion from</p>
              <p className="text-4xl font-bold text-blue-600 mb-2">£40</p>
              <p className="text-gray-600 text-sm">Up to 3 rooms • Additional rooms £5 each</p>
            </div>
          </div>
          <p className="text-gray-600 mb-6">
            Optional extras include colour floor plans, 3D visualisations, furniture layouts, and symbols. 
            Use our pricing calculator for an instant quote.
          </p>
          <Link to="/pricing">
            <Button variant="outline" size="lg">
              View Full Pricing <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Turnaround Times */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Turnaround times</h2>
          <div className="flex items-start mb-6">
            <Clock className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-gray-700 font-medium">Standard delivery: 5-7 working days</p>
              <p className="text-gray-600">Most conversions are completed within this timeframe. Complex projects may take slightly longer.</p>
            </div>
          </div>
          <p className="text-gray-600">
            We'll contact you if we need any clarification on your sketch, which may affect delivery time. 
            Clear sketches with measurements are processed fastest.
          </p>
        </div>
      </section>

      {/* What We Need From You */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What we need from you</h2>
          <p className="text-gray-600 mb-6">
            To convert your sketch accurately, please provide:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-700 font-medium">A clear photo or scan of your sketch</span>
                <p className="text-gray-600 text-sm">Phone photos are fine - just ensure the sketch is clearly visible</p>
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-700 font-medium">Any known dimensions</span>
                <p className="text-gray-600 text-sm">Room sizes, wall lengths, window/door positions if you have them</p>
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-700 font-medium">Notes on what must be shown</span>
                <p className="text-gray-600 text-sm">Walls, doors, windows, room names - anything specific you need on the final plan</p>
              </div>
            </li>
          </ul>
          <p className="text-gray-600 mt-6 text-sm bg-green-50 p-4 rounded-lg">
            <strong>Tip:</strong> Not sure how to measure? Our <Link to="/order?guide_only=true" className="text-blue-600 hover:underline">£2 measuring guide</Link> includes step-by-step instructions - and it's free when you order a floor plan.
          </p>
        </div>
      </section>

      {/* Examples */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Examples</h2>
          <p className="text-gray-600 mb-8">
            See real before-and-after examples of sketches we've converted to professional CAD floor plans.
          </p>
          <div className="space-y-12">
            {examples.map((example) => (
              <div key={example.id} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{example.title}</h3>
                <p className="text-gray-600 mb-6">{example.caption}</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-3">Before (Customer Sketch)</p>
                    {example.beforeImage.type === 'image' ? (
                      <img 
                        src={example.beforeImage.url} 
                        alt={example.beforeImage.alt}
                        className="w-full h-auto rounded-lg shadow"
                      />
                    ) : (
                      <PDFViewer 
                        url={example.beforeImage.url} 
                        title={example.beforeImage.alt}
                        rotation={example.beforeImage.rotation || 0}
                      />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-3">After (Professional CAD)</p>
                    {example.afterImage.type === 'image' ? (
                      <img 
                        src={example.afterImage.url} 
                        alt={example.afterImage.alt}
                        className="w-full h-auto rounded-lg shadow"
                      />
                    ) : (
                      <PDFViewer 
                        url={example.afterImage.url} 
                        title={example.afterImage.alt}
                        rotation={example.afterImage.rotation || 0}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery">
              <Button variant="outline">
                View Full Gallery <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">FAQs</h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start">
                  <HelpCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to convert your sketch?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Upload your sketch and get professional CAD floor plans from just £40.
          </p>
          <Link to="/order">
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
              Start Your Order <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SketchToCADConversionUK;
